import { LevelModel } from 'shared/domain/level/types/model';
import { LevelOnView } from 'shared/domain/level/types/view';
import { LabelledEntity, HashMap } from 'shared/types/commonView';
import { compareObjectIds } from 'shared/utils/id/compare';

/**
 * Check if level has a map for given documentation and version
 */
export function hasDocumentation(
  level: LevelModel,
  documentationId: string | undefined,
  versionId: string | undefined
): boolean {
  return level.maps.some(
    (map) => map.documentationId === documentationId && map.versionId === versionId
  );
}

/**
 * Find levels on a specific site that have given documentation
 */
export function findLevelsForSiteAndDocumentation(
  levels: LevelModel[],
  siteId: string,
  documentationId: string | undefined,
  versionId: string | undefined
): LabelledEntity[] {
  return levels
    .filter((lvl) => lvl.site === siteId && hasDocumentation(lvl, documentationId, versionId))
    .map((lvl) => ({ _id: lvl._id, label: lvl.label }));
}

export function findDocumentationOnLevelsAndSites(
  levels: LevelModel[],
  sites: LabelledEntity[],
  documentationId: string | undefined,
  versionId: string | undefined
): { levelsValue: LabelledEntity[]; sitesValue: LabelledEntity[] } {
  const siteIds: string[] = [];

  const levelsValue = levels.reduce(
    (result: LabelledEntity[], level): LabelledEntity[] => {
      const isDocumentationOnLevel = hasDocumentation(
        level,
        documentationId,
        versionId
      );
      if (
        isDocumentationOnLevel &&
        !result.some((entry) =>
          compareObjectIds(entry._id, level._id, 'findDocumentationOnLevels')
        )
      ) {
        result.push({ _id: level._id, label: level.label });
        siteIds.push(level.site);
      }

      return result;
    },
    [] as LabelledEntity[]
  );

  const sitesValue = sites.reduce(
    (result: LabelledEntity[], site: LabelledEntity): LabelledEntity[] => {
      if (siteIds.includes(site._id)) {
        result.push({ _id: site._id, label: site.label });
      }
      return result;
    },
    []
  );

  return { levelsValue, sitesValue };
}

/**
 * Filter existing levelsPerSite HashMap to only include levels with given documentation
 */
export function filterLevelsPerSiteByDocumentation(
  levels: LevelModel[],
  levelsPerSite: HashMap<LevelOnView[]>,
  documentationId: string | undefined,
  versionId: string | undefined
): HashMap<LevelOnView[]> {
  // Create a Set of level IDs that have the documentation
  const levelIdsWithDoc = new Set<string>();
  
  levels.forEach((level) => {
    if (hasDocumentation(level, documentationId, versionId)) {
      levelIdsWithDoc.add(level._id);
    }
  });

  // Filter the existing levelsPerSite
  const result: HashMap<LevelOnView[]> = {};
  
  Object.keys(levelsPerSite).forEach((siteId) => {
    const filteredLevels = levelsPerSite[siteId].filter((levelOnView) =>
      levelIdsWithDoc.has(levelOnView._id)
    );
    
    if (filteredLevels.length > 0) {
      result[siteId] = filteredLevels;
    }
  });

  return result;
}
